import type { EquipmentSlots } from "../core/types";
import type { NoteEntry, NotebookSection } from "../data/content-types";
import { SaveManager } from "../core/SaveManager";

export interface ChapterProgress {
  talked: string[];
  inspected: string[];
  hazards: string[];
  objectives: Record<string, boolean>;
  /** Puntuación de la evaluación de riesgos por hotspot (0-5). */
  scores: Record<string, number>;
  equipped: boolean;
  extraDone: boolean;
  done: boolean;
}

const NOTEBOOK_SECTIONS: NotebookSection[] = [
  "personas",
  "casos",
  "objetos",
  "senales",
  "epis",
  "pistas",
  "expedientes",
];

function emptySlots(): EquipmentSlots {
  return { head: null, eyes: null, ears: null, respiratory: null, torso: null, hands: null, feet: null, fall: null };
}

function emptyNotebook(): Record<NotebookSection, NoteEntry[]> {
  const nb = {} as Record<NotebookSection, NoteEntry[]>;
  for (const s of NOTEBOOK_SECTIONS) nb[s] = [];
  return nb;
}

/**
 * Estado central del juego (fuente única de verdad). Persiste a través
 * de SaveManager. Los gestores (Inventory, Equipment, Mission...) operan
 * sobre esta instancia; la UI la lee.
 */
export class GameState {
  inventory: string[] = [];
  equipment: EquipmentSlots = emptySlots();
  flags: Record<string, boolean> = {};
  notebook: Record<NotebookSection, NoteEntry[]> = emptyNotebook();
  security: Record<string, number> = {};
  progress: Record<string, ChapterProgress> = {};

  constructor() {
    this.hydrate();
  }

  /** Progreso de un capítulo/localización (lo crea si no existe). */
  chapter(location: string): ChapterProgress {
    const p = (this.progress[location] ??= {
      talked: [],
      inspected: [],
      hazards: [],
      objectives: {},
      scores: {},
      equipped: false,
      extraDone: false,
      done: false,
    });
    p.scores ??= {}; // compatibilidad con guardados antiguos
    return p;
  }

  setFlag(flag: string, value = true): void {
    this.flags[flag] = value;
    this.persist();
  }

  hasFlag(flag: string): boolean {
    return !!this.flags[flag];
  }

  /** Añade una nota al cuaderno evitando duplicados por (sección, título). */
  addNote(note: NoteEntry): boolean {
    const list = this.notebook[note.section];
    if (list.some((n) => n.heading === note.heading)) return false;
    list.push(note);
    this.persist();
    return true;
  }

  persist(): void {
    SaveManager.patch({
      inventory: this.inventory,
      equipment: this.equipment as unknown as Record<string, string | null>,
      flags: this.flags,
      missions: {
        progress: this.progress,
        notebook: this.notebook,
        security: this.security,
      },
    });
  }

  private hydrate(): void {
    const save = SaveManager.get();
    if (Array.isArray(save.inventory)) this.inventory = [...save.inventory];
    if (save.equipment) this.equipment = { ...emptySlots(), ...(save.equipment as Partial<EquipmentSlots>) };
    if (save.flags) this.flags = { ...save.flags };
    const m = save.missions as
      | {
          progress?: Record<string, ChapterProgress>;
          notebook?: Record<NotebookSection, NoteEntry[]>;
          security?: Record<string, number>;
        }
      | undefined;
    if (m?.progress) this.progress = m.progress;
    if (m?.notebook) this.notebook = { ...emptyNotebook(), ...m.notebook };
    if (m?.security) this.security = m.security;
  }
}
